import { useWebSocket } from "../useWebSocket";

const channel = "ConnectionRequestChannel";

export interface UpdatePendingRequestsCountEvent {
  event: "updatePendingRequestsCount";
  pending_requests_count: number;
}

export interface ConnectionStatusChangedEvent {
  connection_id: number;
  event: "connectionStatusChanged";
  initiator_public_uid: string;
  recipient_public_uid: string | null;
  status: string;
}

export type ConnectionRequestWebSocketEvent =
  | UpdatePendingRequestsCountEvent
  | ConnectionStatusChangedEvent;

/**
 * Hook for subscribing to connection request WebSocket events.
 * Provides real-time updates for connection request counts and status changes.
 *
 * @param communityMemberId - ID of the community member to subscribe for. If undefined, subscription is not created.
 * @param onMessageReceive - Callback function that handles incoming WebSocket messages.
 *                           Receives either UpdatePendingRequestsCountEvent or ConnectionStatusChangedEvent.
 *
 * @example
 * ```tsx
 * useConnectionRequestWebSocket(
 *   currentCommunityMember?.id,
 *   (eventData) => {
 *     if (eventData.event === "updatePendingRequestsCount") {
 *       setPendingCount(eventData.pending_requests_count);
 *     }
 *   }
 * );
 * ```
 */
export const useConnectionRequestWebSocket = (
  communityMemberId: number | undefined,
  onMessageReceive: (message: ConnectionRequestWebSocketEvent) => void,
) => {
  useWebSocket(
    {
      channel,
      onMessageReceive,
      community_member_id: communityMemberId,
      canCreateConnection: !!communityMemberId,
    },
    [communityMemberId],
  );
};
